import {
  all,
  fork,
  getContext,
  put,
  race,
  select,
  take,
} from 'redux-saga/effects';
import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { buffers, eventChannel } from 'redux-saga';
import { PapiSession } from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import { flagEnabled } from '@wix/da-react-context/pkg/flags/redux/selectors';
import { getHubState } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { receiveHubMessage } from '@wix/da-shared-react/pkg/redux/hub/actions';
import { appVisibilityChange } from '@wix/da-shared-react/pkg/redux/app/actions';

function* listenToHubChannels() {
  const isSupabaseHub = yield select(flagEnabled, 'supabase_hub');
  const client: SupabaseClient = yield getContext('supaClient');

  if (!isSupabaseHub) {
    return;
  }

  const hub: PapiSession['hub'] = yield select(getHubState);

  if (!hub || !hub.subs || hub.subs.length === 0) {
    return;
  }

  // Don't subscribe if page is hidden - wait for it to become visible
  if (typeof document !== 'undefined' && document.hidden) {
    ddt.log(
      'hub',
      'Realtime hub channels not started, page is hidden. Waiting for visibility...'
    );
    // Wait for visibility change to visible
    const visibilityAction = yield take(appVisibilityChange);
    if (visibilityAction.payload.hidden) {
      // Still hidden, return (shouldn't happen but handle gracefully)
      ddt.log('hub', 'Document still hidden after visibility change event');
      return;
    }
    ddt.log('hub', 'Document became visible, starting hub channels');
  }

  try {
    const hubChannels: RealtimeChannel[] = [];

    const sagaChannel = eventChannel(emitter => {
      // Create a channel for each subscription in hub.subs
      for (const channelName of hub.subs) {
        if (!channelName) {
          continue;
        }

        try {
          const channel = client.realtime.channel(channelName, {
            config: {
              broadcast: {
                ack: false,
                self: false,
              },
            },
          });

          // Listen to broadcast events on this channel
          // Using 'message' as the generic event name for hub channels
          channel.on('broadcast', { event: 'broadcast' }, payload => {
            emitter({
              channel: channelName,
              data: payload,
            });
          });

          channel.subscribe((status, err) => {
            ddt.log(
              'hub',
              `Hub channel ${channelName} subscription status changed`,
              {
                status,
                err,
              }
            );

            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              if (!document.hidden) {
                ddt.log(
                  'hub',
                  'Hub channel error detected, triggering recovery',
                  {
                    channelName,
                    status,
                    err,
                  }
                );
                channel?.rejoinTimer?.scheduleTimeout();
              }
            }

            if (status === 'SUBSCRIBED') {
              ddt.log(
                'hub',
                `Successfully subscribed to hub channel ${channelName}`
              );
            }

            if (status === 'CLOSED') {
              ddt.log('hub', `Hub channel ${channelName} closed`);
            }

            if (err && err.message !== '') {
              ddt.error(
                'hub',
                `Realtime subscription error for hub channel ${channelName} - ${status}`,
                err
              );
            }
          });

          hubChannels.push(channel);
        } catch (err) {
          ddt.error('hub', `Error creating hub channel ${channelName}`, err);
        }
      }

      // Return cleanup function
      return () => {
        for (const channel of hubChannels) {
          try {
            channel.unsubscribe();
            client.removeChannel(channel);
          } catch (err) {
            ddt.error('hub', 'Error cleaning up hub channel', err);
          }
        }
        hubChannels.length = 0;
      };
    }, buffers.expanding<any>());

    while (true) {
      try {
        const payload = yield race({
          chan: take(sagaChannel),
        });

        if (payload.chan) {
          ddt.log('hub', 'Realtime event received', payload.chan);

          const { channel, data } = payload.chan;
          if (channel && data) {
            // Parse channel name from Supabase format: hub:obfuscated:channelname
            // Example: hub:RqvrnIaSvXUdVR1TiYcSbw:mcfeedbacknew -> mcfeedbacknew
            let channelName = channel;
            if (channel.startsWith('hub:')) {
              const parts = channel.split(':');
              if (parts.length >= 3) {
                // Extract the last part which is the actual channel name
                channelName = parts[parts.length - 1];
              }
            }

            yield put(receiveHubMessage(channelName, data));
          }
        }
      } catch (err) {
        ddt.error('hub', 'Realtime processing error', err);
        sagaChannel.close();
      }
    }
  } catch (err) {
    ddt.error('hub', 'Error creating saga channel', err);
  }
}

/**
 * Listens to realtime events for hub notifications
 */
export function* hubSaga() {
  yield all([fork(listenToHubChannels)]);
}
